import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useContentNavigationOptional } from "@/context/ContentNavigationContext";
import { useDocTitle } from "@/context/DocTitleContext";
import type { SourceCitation } from "@/lib/chat/citations";

interface SourceCitationBadgeProps {
  citation: SourceCitation;
  /**
   * When the chat is not mounted alongside a viewer (e.g. Standalone Chat),
   * provide the contentId so clicking the badge navigates to /content/:id.
   * If the citation already carries a contentId (multi-doc chat), it wins.
   */
  fallbackContentId?: string;
}

function formatMmSs(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

function truncateTitle(title: string, max = 24): string {
  if (title.length <= max) return title;
  return title.slice(0, max - 1).trimEnd() + "…";
}

export const SourceCitationBadge: React.FC<SourceCitationBadgeProps> = ({
  citation,
  fallbackContentId,
}) => {
  const nav = useContentNavigationOptional();
  const navigate = useNavigate();
  const targetContentId = citation.contentId ?? fallbackContentId;
  const docTitle = useDocTitle(targetContentId);

  const isDoc = citation.kind === "doc";
  const isPage = citation.kind === "page";
  const isRange = isPage && citation.endValue !== undefined && citation.endValue !== citation.value;

  const label = useMemo(() => {
    if (isDoc) return docTitle ? truncateTitle(docTitle) : "Source";
    if (isPage) {
      return citation.endValue !== undefined && citation.endValue !== citation.value
        ? `P.${citation.value}–P.${citation.endValue}`
        : `P.${citation.value}`;
    }
    return formatMmSs(citation.value);
  }, [citation, isDoc, isPage, docTitle]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Doc-only citation — open the source viewer at top
      if (isDoc) {
        if (nav && !citation.contentId) {
          nav.navigateToPage(1);
          return;
        }
        if (targetContentId) navigate(`/content/${targetContentId}`);
        return;
      }

      // Same-pane viewer (Content page, Room with viewer mounted)
      if (nav && !citation.contentId) {
        if (isPage) nav.navigateToPage(citation.value);
        else nav.navigateToTimestamp(citation.value);
        return;
      }

      // Cross-document or no-viewer: navigate to the content page with anchor params
      if (targetContentId) {
        const search = isPage
          ? `?page=${citation.value}`
          : `?t=${citation.value}`;
        navigate(`/content/${targetContentId}${search}`);
      }
    },
    [nav, navigate, citation, isPage, isDoc, targetContentId],
  );

  const ariaLabel = isDoc
    ? `Open source ${docTitle || ""}`.trim()
    : isRange
    ? `Jump to pages ${citation.value} through ${citation.endValue}`
    : `Jump to ${label}`;

  return (
    <HoverCard openDelay={300} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          aria-label={ariaLabel}
          className="
            inline-flex items-center justify-center align-baseline
            mx-0.5 h-5 px-1.5 gap-1
            rounded-md bg-muted/70
            text-[11px] font-medium leading-none text-muted-foreground tabular-nums
            no-underline cursor-pointer
            transition-colors duration-150
            hover:bg-muted hover:text-foreground
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
            focus-visible:ring-offset-1 focus-visible:ring-offset-background
          "
        >
          {isDoc && <FileText className="h-3 w-3" aria-hidden="true" />}
          <span className="max-w-[14ch] truncate">{label}</span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="center"
        className="w-auto max-w-[18rem] p-2.5"
        sideOffset={4}
      >
        <p className="text-xs text-foreground">
          {isDoc ? (
            <>
              Open <span className="font-medium">{docTitle || "source document"}</span>
              {targetContentId ? "." : "."}
            </>
          ) : (
            <>
              {isRange ? "Open pages " : isPage ? "Open page " : "Jump to "}
              <span className="font-medium">{label}</span>
              {nav && !citation.contentId
                ? " in the viewer."
                : targetContentId
                ? " in the source."
                : "."}
            </>
          )}
        </p>
      </HoverCardContent>
    </HoverCard>
  );
};
